import { Link } from "react-router-dom";
import { Brain, Shield, Crown, BookOpen, Users, Award, ArrowRight, HeartPulse, TrendingUp, GraduationCap } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";

const LOGO_MEN =
  "https://customer-assets.emergentagent.com/job_491658ab-5b7f-4e73-96ad-15d40bd32385/artifacts/bl8jhxxf_file_00000000a58871f5bb9b7b728fcb5be0.png";
const LOGO_WOMEN = "/images/logo-women-pink.png";

const HERO_BG =
  "https://images.unsplash.com/photo-1771011521821-e3d984f30522?crop=entropy&cs=srgb&fm=jpg&q=85";

const PILLARS = [
  { title: "Think.", desc: "Build awareness. Change your mindset.", icon: Brain },
  { title: "Grow.", desc: "Develop discipline. Strengthen your character.", icon: Shield },
  { title: "Lead.", desc: "Use your growth to lead and uplift others.", icon: Crown },
];

const TRACKS = [
  { n: 1, title: "Self-Awareness & Mental Wellness", icon: Brain, slug: "self-awareness" },
  { n: 2, title: "Trauma & Healing Education", icon: HeartPulse, slug: "trauma-aces" },
  { n: 3, title: "Victim Impact & Accountability", icon: Users, slug: "victim-impact" },
  { n: 4, title: "History, Culture & Systemic Awareness", icon: BookOpen, slug: "history-systems" },
  { n: 5, title: "Education & Intellectual Development", icon: GraduationCap, slug: "education" },
  { n: 6, title: "Economic Empowerment", icon: TrendingUp, slug: "economic-empowerment" },
  { n: 7, title: "Leadership & Mentorship", icon: Crown, slug: "leadership" },
];

export default function Landing() {
  const { effectiveProgram } = useAuth();
  const isWomen = effectiveProgram === "women";
  const LOGO_URL = isWomen ? LOGO_WOMEN : LOGO_MEN;
  return (
    <div data-testid="landing-page">
      <Seo
        path="/"
        title="Trauma-Informed Curriculum for Justice-Impacted People"
        description="The Crown Path Initiative is a structured, trauma-informed leadership and rehabilitation curriculum for justice-impacted men, women, and young people. Seven tracks, real citations, certification, and community — founded by Almeer Nance."
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden grain" data-hero-dark>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908]/85 via-[#0A0908]/75 to-[#0A0908]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 grid lg:grid-cols-2 gap-16 items-center">
          <div className="fade-up">
            <div className="overline mb-6">Institutional Implementation Program</div>
            <h1
              className="text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-6 text-[#F7F5F0]"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              On the path to <span className="text-[#C9A961] italic">earning our crowns.</span>
            </h1>
            <p className="text-lg text-[#D1CFC9] max-w-xl mb-10 leading-relaxed">
              A trauma-informed leadership and rehabilitation curriculum — with dedicated pathways for
              men &amp; young men and for women &amp; young girls — built for nonprofit groups, reentry
              programs, and institutions to transform lived experience into lasting leadership.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register" className="btn-primary" data-testid="hero-enroll-btn">
                Enroll Now
              </Link>
              <Link to="/about" className="btn-secondary" data-testid="hero-learn-btn">
                Learn More
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-8 text-sm text-[#8A8782]">
              <div><span className="text-[#C9A961] font-semibold">7</span> Structured Tracks</div>
              <div><span className="text-[#C9A961] font-semibold">5</span> Certification Levels</div>
              <div><span className="text-[#C9A961] font-semibold">21</span> Sessions to Transform</div>
            </div>
          </div>

          <div className="relative flex justify-center items-center fade-up" style={{ animationDelay: "200ms" }}>
            <div className="absolute inset-0 bg-[#C9A961]/10 blur-3xl rounded-full" />
            <img src={LOGO_URL} alt="The Crown Path Initiative" className="relative max-w-md w-full" />
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative bg-tcpi-bg py-24 md:py-32 grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="overline mb-4">Our Foundation</div>
            <h2 className="text-4xl md:text-5xl text-[#F7F5F0]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Think. Grow. Lead.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {PILLARS.map(({ title, desc, icon: Icon }) => (
              <div key={title} className="card-bento text-center" data-testid={`pillar-${title.toLowerCase().replace('.', '')}`}>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-[#C9A961]/40 flex items-center justify-center">
                  <Icon className="w-8 h-8 text-[#C9A961]" strokeWidth={1.5} />
                </div>
                <h3 className="text-3xl mb-3 text-[#C9A961]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  {title}
                </h3>
                <p className="text-[#D1CFC9]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 TRACKS PREVIEW */}
      <section className="relative bg-[#121110] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="overline mb-4">The Curriculum</div>
            <h2 className="text-4xl md:text-5xl text-[#F7F5F0] mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Seven Tracks. One Path. One Crown.
            </h2>
            <p className="text-[#D1CFC9] max-w-2xl mx-auto">
              Each track is delivered over a Monday–Wednesday–Friday rhythm: Learn & Reflect,
              Apply & Assess, Share & Lead.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TRACKS.map(({ n, title, icon: Icon, slug }) => (
              <div key={slug} className="card-bento" data-testid={`track-card-${slug}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="overline text-xs">Track {n}</span>
                  <Icon className="w-5 h-5 text-[#C9A961]" strokeWidth={1.5} />
                </div>
                <h3
                  className="text-xl text-[#F7F5F0] leading-snug mb-4"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {title}
                </h3>
                <Link
                  to="/register"
                  className="text-sm text-[#C9A961] hover:underline inline-flex items-center gap-1"
                  data-testid={`landing-track-link-${slug}`}
                >
                  Enroll to begin <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-tcpi-bg py-24 md:py-32 grain">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Award className="w-12 h-12 text-[#C9A961] mx-auto mb-6" strokeWidth={1.2} />
          <h2 className="text-4xl md:text-5xl text-[#F7F5F0] mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            "People are more than the worst decision they have made —
            <br />
            <span className="text-[#C9A961] italic">but transformation requires intentional effort.</span>"
          </h2>
          <p className="text-sm tracking-widest text-[#8A8782] uppercase mb-10" data-testid="landing-founder-credit">
            — Almeer Nance, Founder ·{" "}
            <Link to="/founder" className="text-[#C9A961] hover:underline normal-case tracking-normal">
              Read his story
            </Link>
          </p>
          <div className="gold-divider w-32 mx-auto my-10" />
          <p className="text-lg text-[#D1CFC9] mb-10">
            For nonprofit groups, reentry programs, and institutions ready to invest in the
            people their communities need.
          </p>
          <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="cta-enroll-btn">
            Begin the Path <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-[#121110] py-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <NewsletterSignup source="landing" />
        </div>
      </section>
    </div>
  );
}
